<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import HistoryCoursesAPI from 'dashboard/api/historyCourses';
import TaxiDriversAPI from 'dashboard/api/taxiDrivers';
import TaxiClientsAPI from 'dashboard/api/taxiClients';
import TaxiPartnersAPI from 'dashboard/api/taxiPartners';
import TaxiZonesAPI from 'dashboard/api/taxiZones';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const { accountId } = useAccount();

/** URL webhook n8n (alertes WhatsApp/partenaires). Vide = désactivé. Sinon défaut fork k2o. */
const assignmentAlertWebhookUrl = () => {
  const raw = import.meta.env.VITE_CHATWOOT_TAXIS_ASSIGNMENT_ALERT_URL;
  if (raw === '') return '';
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim();
  return 'https://n8n.k2o.ai/webhook/taxi-assignment-changed-alert';
};

const courses = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const dialogRef = ref(null);
const editing = ref(null);

const driversCatalog = ref([]);
const clientsCatalog = ref([]);
const partnersCatalog = ref([]);
const zonesCatalog = ref([]);

const selectClass =
  'h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-8';

const form = ref({
  status: 'requested',
  requested_at: '',
  passenger_count: 1,
  taxi_driver_id: '',
  taxi_client_id: '',
  taxi_partner_id: '',
  taxi_zone_id: '',
  pickup_address: '',
  dropoff_address: '',
  fare_amount: '',
  currency: 'EUR',
  payment_method: '',
  payment_status: 'pending',
  notes: '',
});

const statusOptions = computed(() => [
  {
    value: 'requested',
    label: t('TAXIS.HISTORY.STATUS.REQUESTED'),
    color: 'blue',
    icon: 'i-lucide-clock',
  },
  {
    value: 'dispatched',
    label: t('TAXIS.HISTORY.STATUS.DISPATCHED'),
    color: 'purple',
    icon: 'i-lucide-send',
  },
  {
    value: 'en_route',
    label: t('TAXIS.HISTORY.STATUS.EN_ROUTE'),
    color: 'amber',
    icon: 'i-lucide-navigation',
  },
  {
    value: 'arrived',
    label: t('TAXIS.HISTORY.STATUS.ARRIVED'),
    color: 'teal',
    icon: 'i-lucide-map-pin-check',
  },
  {
    value: 'on_trip',
    label: t('TAXIS.HISTORY.STATUS.ON_TRIP'),
    color: 'green',
    icon: 'i-lucide-car',
  },
  {
    value: 'completed',
    label: t('TAXIS.HISTORY.STATUS.COMPLETED'),
    color: 'green',
    icon: 'i-lucide-circle-check',
  },
  {
    value: 'cancelled',
    label: t('TAXIS.HISTORY.STATUS.CANCELLED'),
    color: 'ruby',
    icon: 'i-lucide-circle-x',
  },
  {
    value: 'no_show',
    label: t('TAXIS.HISTORY.STATUS.NO_SHOW'),
    color: 'slate',
    icon: 'i-lucide-user-x',
  },
]);

const paymentStatusOptions = computed(() => [
  {
    value: 'pending',
    label: t('TAXIS.HISTORY.PAYMENT_STATUS.PENDING'),
    color: 'amber',
    icon: 'i-lucide-clock',
  },
  {
    value: 'paid',
    label: t('TAXIS.HISTORY.PAYMENT_STATUS.PAID'),
    color: 'green',
    icon: 'i-lucide-circle-check',
  },
  {
    value: 'failed',
    label: t('TAXIS.HISTORY.PAYMENT_STATUS.FAILED'),
    color: 'ruby',
    icon: 'i-lucide-alert-circle',
  },
  {
    value: 'refunded',
    label: t('TAXIS.HISTORY.PAYMENT_STATUS.REFUNDED'),
    color: 'blue',
    icon: 'i-lucide-undo',
  },
]);

const resetForm = () => {
  const now = new Date();
  const pad = n => String(n).padStart(2, '0');
  const localDefault = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  form.value = {
    status: 'requested',
    requested_at: localDefault,
    passenger_count: 1,
    taxi_driver_id: '',
    taxi_client_id: '',
    taxi_partner_id: '',
    taxi_zone_id: '',
    pickup_address: '',
    dropoff_address: '',
    fare_amount: '',
    currency: 'EUR',
    payment_method: '',
    payment_status: 'pending',
    notes: '',
  };
  editing.value = null;
};

const toDatetimeLocal = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const dash = () => t('TAXIS.HISTORY.DASH');

const clientLabel = c => {
  if (!c) return dash();
  const n = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return n || dash();
};

const formatWhen = d => {
  if (!d) return dash();
  try {
    return new Date(d).toLocaleString();
  } catch {
    return String(d);
  }
};

const statusConfig = s => {
  const opt = statusOptions.value.find(o => o.value === s);
  return (
    opt || {
      label: s || dash(),
      color: 'slate',
      icon: 'i-lucide-circle',
    }
  );
};

const paymentStatusConfig = code => {
  const opt = paymentStatusOptions.value.find(o => o.value === code);
  return (
    opt || {
      label: code || dash(),
      color: 'slate',
      icon: 'i-lucide-circle',
    }
  );
};

const addrPreview = (txt, max = 40) => {
  const a = txt?.trim();
  if (!a) return dash();
  return a.length > max ? `${a.slice(0, max - 1)}…` : a;
};

const courseMatchesQuery = (row, rawQ) => {
  if (!rawQ.trim()) return true;
  const q = rawQ.toLowerCase();
  const blob = [
    row.taxi_driver?.name,
    row.taxi_client?.first_name,
    row.taxi_client?.last_name,
    row.taxi_partner?.name,
    row.taxi_zone?.name,
    row.pickup_address,
    row.dropoff_address,
    row.status,
    row.payment_status,
    row.notes,
    row.scheduled_pickup_at ? formatWhen(row.scheduled_pickup_at) : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return blob.includes(q);
};

const baseFiltered = computed(() =>
  courses.value.filter(row => courseMatchesQuery(row, searchQuery.value))
);

/** Courses marquées programmées côté n8n / API (metadata.is_scheduled + heure prévue). */
const isScheduledFlagged = meta => {
  if (!meta || typeof meta !== 'object') return false;
  const v = meta.is_scheduled;
  return v === true || v === 'true' || v === 1 || v === '1';
};

const isProgrammedTrip = row =>
  Boolean(row.scheduled_pickup_at) && isScheduledFlagged(row.metadata);

const programmedCoursesSorted = computed(() => {
  const rows = baseFiltered.value.filter(isProgrammedTrip);
  const now = Date.now();
  return [...rows].sort((a, b) => {
    const ta = new Date(a.scheduled_pickup_at).getTime();
    const tb = new Date(b.scheduled_pickup_at).getTime();
    const aFut = ta >= now;
    const bFut = tb >= now;
    if (aFut && bFut) return ta - tb;
    if (aFut && !bFut) return -1;
    if (!aFut && bFut) return 1;
    return tb - ta;
  });
});

/** Journal « toutes les courses » hors lignes déjà affichées en Programmé. */
const tableCourses = computed(() =>
  baseFiltered.value.filter(row => !isProgrammedTrip(row))
);

const fetchCatalogs = async () => {
  try {
    const [dr, cl, pr, zn] = await Promise.all([
      TaxiDriversAPI.getAll(),
      TaxiClientsAPI.getAll(),
      TaxiPartnersAPI.getAll(),
      TaxiZonesAPI.getAll(),
    ]);
    driversCatalog.value = (dr.data || []).map(d => ({
      id: d.id,
      name: d.name,
      phone: d.phone ?? '',
    }));
    clientsCatalog.value = cl.data || [];
    partnersCatalog.value = (pr.data || []).map(p => ({
      id: p.id,
      name: p.name,
      phone: p.phone ?? '',
    }));
    zonesCatalog.value = (zn.data || []).map(z => ({ id: z.id, name: z.name }));
  } catch {
    driversCatalog.value = [];
    clientsCatalog.value = [];
    partnersCatalog.value = [];
    zonesCatalog.value = [];
  }
};

const fetchCourses = async () => {
  loading.value = true;
  try {
    const { data } = await HistoryCoursesAPI.getAll();
    courses.value = data;
  } catch {
    useAlert(t('TAXIS.HISTORY.API.ERROR_FETCH'));
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  await fetchCatalogs();
  await fetchCourses();
});

const openCreate = () => {
  resetForm();
  dialogRef.value?.open();
};

const openEdit = row => {
  editing.value = row;
  form.value = {
    status: row.status || 'requested',
    requested_at: toDatetimeLocal(row.requested_at),
    passenger_count: row.passenger_count ?? 1,
    taxi_driver_id: row.taxi_driver_id ?? '',
    taxi_client_id: row.taxi_client_id ?? '',
    taxi_partner_id: row.taxi_partner_id ?? '',
    taxi_zone_id: row.taxi_zone_id ?? '',
    pickup_address: row.pickup_address || '',
    dropoff_address: row.dropoff_address || '',
    fare_amount: row.fare_amount != null ? String(row.fare_amount) : '',
    currency: row.currency || 'EUR',
    payment_method: row.payment_method || '',
    payment_status: row.payment_status || 'pending',
    notes: row.notes || '',
  };
  dialogRef.value?.open();
};

const onDialogClose = () => {
  resetForm();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const buildPayload = () => {
  const f = form.value;
  const num = (v, fallback = null) => {
    if (v === '' || v == null) return fallback;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  return {
    status: f.status,
    requested_at: new Date(f.requested_at).toISOString(),
    passenger_count: num(f.passenger_count, 1) || 1,
    taxi_driver_id: f.taxi_driver_id === '' ? null : num(f.taxi_driver_id),
    taxi_client_id: f.taxi_client_id === '' ? null : num(f.taxi_client_id),
    taxi_partner_id: f.taxi_partner_id === '' ? null : num(f.taxi_partner_id),
    taxi_zone_id: f.taxi_zone_id === '' ? null : num(f.taxi_zone_id),
    pickup_address: f.pickup_address?.trim() || null,
    dropoff_address: f.dropoff_address?.trim() || null,
    fare_amount: f.fare_amount === '' ? null : num(f.fare_amount),
    currency: (f.currency || 'EUR').slice(0, 3),
    payment_method: f.payment_method?.trim() || null,
    payment_status: f.payment_status || 'pending',
    notes: f.notes?.trim() || null,
  };
};

const saveCourse = async () => {
  if (!form.value.requested_at) {
    useAlert(t('TAXIS.HISTORY.VALIDATION.REQUESTED_AT_REQUIRED'));
    return;
  }
  try {
    const payload = buildPayload();
    if (editing.value) {
      await HistoryCoursesAPI.update(editing.value.id, payload);
      useAlert(t('TAXIS.HISTORY.API.SUCCESS_UPDATE'));
    } else {
      await HistoryCoursesAPI.create(payload);
      useAlert(t('TAXIS.HISTORY.API.SUCCESS_CREATE'));
    }
    closeDialog();
    await fetchCourses();
  } catch {
    useAlert(t('TAXIS.HISTORY.API.ERROR_SAVE'));
  }
};

const removeCourse = async row => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.HISTORY.CONFIRM_DELETE'))) return;
  try {
    await HistoryCoursesAPI.delete(row.id);
    useAlert(t('TAXIS.HISTORY.API.SUCCESS_DELETE'));
    await fetchCourses();
  } catch {
    useAlert(t('TAXIS.HISTORY.API.ERROR_DELETE'));
  }
};

const ASSIGNMENT_LOCKED_STATUSES = ['completed', 'cancelled', 'no_show'];

const canManageAssignment = row =>
  Boolean(row) && !ASSIGNMENT_LOCKED_STATUSES.includes(row.status);

const assignDialogRef = ref(null);
const assignRow = ref(null);
const assignDriverId = ref('');

const openAssignDialog = row => {
  assignRow.value = row;
  assignDriverId.value =
    row.taxi_driver_id != null && row.taxi_driver_id !== ''
      ? String(row.taxi_driver_id)
      : '';
  assignDialogRef.value?.open();
};

const closeAssignDialog = () => {
  assignDialogRef.value?.close();
};

const onAssignDialogClose = () => {
  assignRow.value = null;
  assignDriverId.value = '';
};

const buildDriverAssignPayload = (row, driverIdNum) => {
  if (driverIdNum == null) {
    return {
      taxi_driver_id: null,
      status: 'requested',
      driver_assigned_at: null,
    };
  }
  const midTrip = ['en_route', 'arrived', 'on_trip'].includes(row.status);
  if (midTrip) {
    return {
      taxi_driver_id: driverIdNum,
      driver_assigned_at: new Date().toISOString(),
    };
  }
  return {
    taxi_driver_id: driverIdNum,
    status: 'dispatched',
    driver_assigned_at: new Date().toISOString(),
  };
};

/** Snapshot chauffeur pour n8n (id + téléphone indispensable pour WhatsApp). */
const driverSnapshotForWebhook = (driverId, nestedFromCourse) => {
  if (driverId == null || driverId === '') return null;
  const sid = typeof driverId === 'number' ? driverId : Number(driverId);
  if (!Number.isFinite(sid)) return null;
  const cat = driversCatalog.value.find(
    d => d.id === sid || String(d.id) === String(driverId)
  );
  const name = nestedFromCourse?.name ?? cat?.name ?? '';
  const phone = nestedFromCourse?.phone ?? cat?.phone ?? '';
  return { id: sid, name, phone };
};

const partnerPayloadForWebhook = row => {
  const pid = row.taxi_partner_id;
  if (pid == null || pid === '') return null;
  const cat = partnersCatalog.value.find(
    p => p.id === pid || String(p.id) === String(pid)
  );
  return {
    id: pid,
    name: row.taxi_partner?.name ?? cat?.name ?? '',
    phone: cat?.phone ?? '',
  };
};

/** POST asynchrone vers n8n après PATCH affectation réussi ; ne doit pas casser le flux UX. */
const postAssignmentAlertToN8n = (row, patch) => {
  const url = assignmentAlertWebhookUrl();
  if (!url) return;

  const prevId = row.taxi_driver_id ?? null;
  const nextId =
    patch.taxi_driver_id !== undefined ? patch.taxi_driver_id : prevId;

  let assignment_event;
  if (prevId == null && nextId != null) assignment_event = 'assigned';
  else if (prevId != null && nextId == null) assignment_event = 'cleared';
  else if (
    prevId != null &&
    nextId != null &&
    String(prevId) !== String(nextId)
  ) {
    assignment_event = 'reassigned';
  } else {
    assignment_event = 'noop';
  }
  if (assignment_event === 'noop') return;

  const nestedMatchesNext =
    row.taxi_driver &&
    nextId != null &&
    Number(row.taxi_driver.id) === Number(nextId);

  const driver_before = driverSnapshotForWebhook(
    prevId,
    row.taxi_driver && Number(row.taxi_driver?.id) === Number(prevId)
      ? row.taxi_driver
      : null
  );
  const driver_after = driverSnapshotForWebhook(
    nextId,
    nestedMatchesNext ? row.taxi_driver : null
  );

  const history_course = {
    id: row.id,
    uuid: row.uuid ?? null,
    status: patch.status !== undefined ? patch.status : row.status,
    scheduled_pickup_at: row.scheduled_pickup_at ?? null,
    pickup_address: row.pickup_address ?? null,
    dropoff_address: row.dropoff_address ?? null,
    conversation_id: row.conversation_id ?? null,
    metadata: row.metadata ?? null,
  };

  const payload = {
    assignment_event,
    changed_at: new Date().toISOString(),
    account_id: Number.isFinite(accountId.value) ? accountId.value : null,
    history_course,
    driver_before,
    driver_after,
    taxi_partner: partnerPayloadForWebhook(row),
    taxi_client: row.taxi_client ?? null,
  };

  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    mode: 'cors',
  }).catch(() => {});
};

const saveAssignment = async () => {
  const row = assignRow.value;
  if (!row) return;
  const raw = assignDriverId.value;
  const nextId = raw === '' || raw == null ? null : Number(raw);
  if (nextId != null && !Number.isFinite(nextId)) {
    useAlert(t('TAXIS.HISTORY.ASSIGNMENT.ERROR'));
    return;
  }
  const prevId = row.taxi_driver_id ?? null;
  if (nextId === prevId) {
    closeAssignDialog();
    return;
  }
  if (nextId == null && prevId != null) {
    // eslint-disable-next-line no-alert, no-restricted-globals
    if (!confirm(t('TAXIS.HISTORY.ASSIGNMENT.CONFIRM_UNASSIGN'))) {
      return;
    }
  }
  try {
    const payload = buildDriverAssignPayload(row, nextId);
    await HistoryCoursesAPI.update(row.id, payload);
    postAssignmentAlertToN8n(row, payload);
    useAlert(
      nextId == null
        ? t('TAXIS.HISTORY.ASSIGNMENT.SUCCESS_CLEARED')
        : t('TAXIS.HISTORY.ASSIGNMENT.SUCCESS_ASSIGNED')
    );
    closeAssignDialog();
    await fetchCourses();
  } catch {
    useAlert(t('TAXIS.HISTORY.ASSIGNMENT.ERROR'));
  }
};

const unlinkAssignment = async () => {
  const row = assignRow.value;
  if (!row?.taxi_driver_id) return;
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.HISTORY.ASSIGNMENT.CONFIRM_UNASSIGN'))) return;
  try {
    const payload = {
      taxi_driver_id: null,
      status: 'requested',
      driver_assigned_at: null,
    };
    await HistoryCoursesAPI.update(row.id, payload);
    postAssignmentAlertToN8n(row, payload);
    useAlert(t('TAXIS.HISTORY.ASSIGNMENT.SUCCESS_CLEARED'));
    closeAssignDialog();
    await fetchCourses();
  } catch {
    useAlert(t('TAXIS.HISTORY.ASSIGNMENT.ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-1 flex-col overflow-auto px-6 py-8">
    <div class="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-ruby-4/50 to-n-ruby-5/40 ring-1 ring-n-ruby-6/60"
            >
              <Icon icon="i-lucide-history" class="size-5 text-n-ruby-11" />
            </div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('TAXIS.HISTORY.TITLE') }}
            </h1>
          </div>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('TAXIS.HISTORY.SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.HISTORY.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          color="ruby"
          @click="openCreate"
        />
      </div>

      <div class="max-w-sm">
        <Input
          v-model="searchQuery"
          type="search"
          :placeholder="t('TAXIS.HISTORY.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-9 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9',
          ]"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>

      <div
        v-if="loading"
        class="flex flex-1 items-center justify-center py-20 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!courses.length"
        class="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-n-ruby-5/30 bg-gradient-to-br from-n-ruby-1/30 to-transparent py-20 text-center"
      >
        <div
          class="flex size-20 items-center justify-center rounded-2xl bg-gradient-to-br from-n-ruby-4/40 to-n-ruby-5/30 ring-4 ring-n-ruby-4/20"
        >
          <Icon icon="i-lucide-calendar-days" class="size-10 text-n-ruby-11" />
        </div>
        <div>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ t('TAXIS.HISTORY.EMPTY_TITLE') }}
          </p>
          <p class="mt-2 max-w-sm text-sm text-n-slate-11">
            {{ t('TAXIS.HISTORY.EMPTY_SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.HISTORY.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="md"
          color="ruby"
          @click="openCreate"
        />
      </div>

      <div
        v-else-if="courses.length && !baseFiltered.length"
        class="flex flex-col items-center justify-center gap-2 rounded-xl border border-n-weak bg-n-solid-1 py-12 text-center"
      >
        <p class="text-sm text-n-slate-11">
          {{ t('TAXIS.HISTORY.NO_SEARCH_RESULTS') }}
        </p>
      </div>

      <div v-else class="flex flex-col gap-8">
        <section
          v-if="programmedCoursesSorted.length"
          class="flex flex-col gap-3"
        >
          <div>
            <h2
              class="flex items-center gap-2 text-base font-semibold text-n-slate-12"
            >
              <div
                class="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-n-purple-4/40 to-n-purple-5/30"
              >
                <Icon
                  icon="i-lucide-calendar-clock"
                  class="size-4 text-n-purple-11"
                />
              </div>
              {{ t('TAXIS.HISTORY.SECTION_PROGRAMMED_TITLE') }}
            </h2>
            <p class="mt-0.5 text-sm text-n-slate-11">
              {{ t('TAXIS.HISTORY.SECTION_PROGRAMMED_SUBTITLE') }}
            </p>
          </div>
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
          >
            <div class="overflow-x-auto">
              <table class="w-full min-w-[960px] text-left text-sm">
                <thead
                  class="border-b border-n-weak bg-n-alpha-2 text-xs font-medium uppercase text-n-slate-11"
                >
                  <tr>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.SCHEDULED_PICKUP') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.STATUS') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.DRIVER') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.CLIENT') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.PARTNER') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.PASSENGERS') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.PICKUP') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.DROPOFF') }}
                    </th>
                    <th class="px-4 py-3 text-end">
                      {{ t('TAXIS.HISTORY.COLUMNS.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="row in programmedCoursesSorted"
                    :key="`p-${row.id}`"
                    class="hover:bg-n-alpha-2/80"
                  >
                    <td
                      class="whitespace-nowrap px-4 py-3 font-medium text-n-slate-12"
                    >
                      {{ formatWhen(row.scheduled_pickup_at) }}
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
                        :class="[
                          statusConfig(row.status).color === 'blue'
                            ? 'bg-n-blue-3/50 text-n-blue-11 ring-1 ring-n-blue-6/50'
                            : statusConfig(row.status).color === 'purple'
                              ? 'bg-n-purple-3/50 text-n-purple-11 ring-1 ring-n-purple-6/50'
                              : statusConfig(row.status).color === 'amber'
                                ? 'bg-n-amber-3/50 text-n-amber-11 ring-1 ring-n-amber-6/50'
                                : statusConfig(row.status).color === 'teal'
                                  ? 'bg-n-teal-3/50 text-n-teal-11 ring-1 ring-n-teal-6/50'
                                  : statusConfig(row.status).color === 'green'
                                    ? 'bg-n-green-3/60 text-n-green-11 ring-1 ring-n-green-6/50'
                                    : statusConfig(row.status).color === 'ruby'
                                      ? 'bg-n-ruby-3/50 text-n-ruby-11 ring-1 ring-n-ruby-6/50'
                                      : 'bg-n-slate-3/50 text-n-slate-11 ring-1 ring-n-slate-6/50',
                        ]"
                      >
                        <Icon
                          :icon="statusConfig(row.status).icon"
                          class="size-3.5"
                        />
                        {{ statusConfig(row.status).label }}
                      </span>
                    </td>
                    <td
                      class="max-w-[10rem] truncate px-4 py-3 text-n-slate-11"
                    >
                      {{ row.taxi_driver?.name || t('TAXIS.HISTORY.DASH') }}
                    </td>
                    <td
                      class="max-w-[10rem] truncate px-4 py-3 text-n-slate-11"
                    >
                      {{
                        row.taxi_client
                          ? clientLabel(row.taxi_client)
                          : t('TAXIS.HISTORY.DASH')
                      }}
                    </td>
                    <td
                      class="max-w-[10rem] truncate px-4 py-3 text-n-slate-11"
                    >
                      {{ row.taxi_partner?.name || t('TAXIS.HISTORY.DASH') }}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-n-slate-11">
                      {{ row.passenger_count ?? t('TAXIS.HISTORY.DASH') }}
                    </td>
                    <td
                      class="max-w-[12rem] truncate px-4 py-3 text-n-slate-11"
                      :title="row.pickup_address || undefined"
                    >
                      {{ addrPreview(row.pickup_address, 36) }}
                    </td>
                    <td
                      class="max-w-[12rem] truncate px-4 py-3 text-n-slate-11"
                      :title="row.dropoff_address || undefined"
                    >
                      {{ addrPreview(row.dropoff_address, 36) }}
                    </td>
                    <td class="px-4 py-3 text-end">
                      <div class="flex justify-end gap-1">
                        <button
                          v-if="canManageAssignment(row)"
                          type="button"
                          class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                          :title="t('TAXIS.HISTORY.ASSIGNMENT.BUTTON_TOOLTIP')"
                          @click="openAssignDialog(row)"
                        >
                          <Icon icon="i-lucide-user-cog" class="size-4" />
                        </button>
                        <button
                          type="button"
                          class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                          :title="t('TAXIS.HISTORY.EDIT')"
                          @click="openEdit(row)"
                        >
                          <Icon icon="i-lucide-pencil" class="size-4" />
                        </button>
                        <button
                          type="button"
                          class="rounded-lg p-2 text-n-ruby-11 hover:bg-n-ruby-3/30"
                          :title="t('TAXIS.HISTORY.DELETE')"
                          @click="removeCourse(row)"
                        >
                          <Icon icon="i-lucide-trash-2" class="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <div
              class="flex size-8 items-center justify-center rounded-lg bg-gradient-to-br from-n-blue-4/40 to-n-blue-5/30"
            >
              <Icon icon="i-lucide-list" class="size-4 text-n-blue-11" />
            </div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{ t('TAXIS.HISTORY.SECTION_ALL_TRIPS_TITLE') }}
            </h2>
          </div>
          <div
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
          >
            <div v-if="tableCourses.length" class="overflow-x-auto">
              <table class="w-full min-w-[1100px] text-left text-sm">
                <thead
                  class="border-b border-n-weak bg-n-alpha-2 text-xs font-medium uppercase text-n-slate-11"
                >
                  <tr>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.REQUESTED_AT') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.STATUS') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.DRIVER') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.CLIENT') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.PARTNER') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.ZONE') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.PICKUP') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.DROPOFF') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.FARE') }}
                    </th>
                    <th class="px-4 py-3">
                      {{ t('TAXIS.HISTORY.COLUMNS.PAYMENT') }}
                    </th>
                    <th class="px-4 py-3 text-end">
                      {{ t('TAXIS.HISTORY.COLUMNS.ACTIONS') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr
                    v-for="row in tableCourses"
                    :key="row.id"
                    class="hover:bg-n-alpha-2/80"
                  >
                    <td class="whitespace-nowrap px-4 py-3 text-n-slate-11">
                      {{ formatWhen(row.requested_at) }}
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
                        :class="[
                          statusConfig(row.status).color === 'blue'
                            ? 'bg-n-blue-3/50 text-n-blue-11 ring-1 ring-n-blue-6/50'
                            : statusConfig(row.status).color === 'purple'
                              ? 'bg-n-purple-3/50 text-n-purple-11 ring-1 ring-n-purple-6/50'
                              : statusConfig(row.status).color === 'amber'
                                ? 'bg-n-amber-3/50 text-n-amber-11 ring-1 ring-n-amber-6/50'
                                : statusConfig(row.status).color === 'teal'
                                  ? 'bg-n-teal-3/50 text-n-teal-11 ring-1 ring-n-teal-6/50'
                                  : statusConfig(row.status).color === 'green'
                                    ? 'bg-n-green-3/60 text-n-green-11 ring-1 ring-n-green-6/50'
                                    : statusConfig(row.status).color === 'ruby'
                                      ? 'bg-n-ruby-3/50 text-n-ruby-11 ring-1 ring-n-ruby-6/50'
                                      : 'bg-n-slate-3/50 text-n-slate-11 ring-1 ring-n-slate-6/50',
                        ]"
                      >
                        <Icon
                          :icon="statusConfig(row.status).icon"
                          class="size-3.5"
                        />
                        {{ statusConfig(row.status).label }}
                      </span>
                    </td>
                    <td
                      class="max-w-[10rem] truncate px-4 py-3 text-n-slate-11"
                    >
                      {{ row.taxi_driver?.name || t('TAXIS.HISTORY.DASH') }}
                    </td>
                    <td
                      class="max-w-[10rem] truncate px-4 py-3 text-n-slate-11"
                    >
                      {{
                        row.taxi_client
                          ? clientLabel(row.taxi_client)
                          : t('TAXIS.HISTORY.DASH')
                      }}
                    </td>
                    <td
                      class="max-w-[10rem] truncate px-4 py-3 text-n-slate-11"
                    >
                      {{ row.taxi_partner?.name || t('TAXIS.HISTORY.DASH') }}
                    </td>
                    <td class="max-w-[8rem] truncate px-4 py-3 text-n-slate-11">
                      {{ row.taxi_zone?.name || t('TAXIS.HISTORY.DASH') }}
                    </td>
                    <td
                      class="max-w-[12rem] truncate px-4 py-3 text-n-slate-11"
                      :title="row.pickup_address || undefined"
                    >
                      {{ addrPreview(row.pickup_address, 36) }}
                    </td>
                    <td
                      class="max-w-[12rem] truncate px-4 py-3 text-n-slate-11"
                      :title="row.dropoff_address || undefined"
                    >
                      {{ addrPreview(row.dropoff_address, 36) }}
                    </td>
                    <td class="whitespace-nowrap px-4 py-3 text-n-slate-11">
                      <template v-if="row.fare_amount != null">
                        {{ row.fare_amount }} {{ row.currency }}
                      </template>
                      <template v-else>{{ t('TAXIS.HISTORY.DASH') }}</template>
                    </td>
                    <td class="px-4 py-3">
                      <span
                        class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium"
                        :class="[
                          paymentStatusConfig(row.payment_status).color ===
                          'amber'
                            ? 'bg-n-amber-3/50 text-n-amber-11 ring-1 ring-n-amber-6/50'
                            : paymentStatusConfig(row.payment_status).color ===
                                'green'
                              ? 'bg-n-green-3/60 text-n-green-11 ring-1 ring-n-green-6/50'
                              : paymentStatusConfig(row.payment_status)
                                    .color === 'ruby'
                                ? 'bg-n-ruby-3/50 text-n-ruby-11 ring-1 ring-n-ruby-6/50'
                                : paymentStatusConfig(row.payment_status)
                                      .color === 'blue'
                                  ? 'bg-n-blue-3/50 text-n-blue-11 ring-1 ring-n-blue-6/50'
                                  : 'bg-n-slate-3/50 text-n-slate-11 ring-1 ring-n-slate-6/50',
                        ]"
                      >
                        <Icon
                          :icon="paymentStatusConfig(row.payment_status).icon"
                          class="size-3.5"
                        />
                        {{ paymentStatusConfig(row.payment_status).label }}
                      </span>
                    </td>
                    <td class="px-4 py-3 text-end">
                      <div class="flex justify-end gap-1">
                        <button
                          v-if="canManageAssignment(row)"
                          type="button"
                          class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                          :title="t('TAXIS.HISTORY.ASSIGNMENT.BUTTON_TOOLTIP')"
                          @click="openAssignDialog(row)"
                        >
                          <Icon icon="i-lucide-user-cog" class="size-4" />
                        </button>
                        <button
                          type="button"
                          class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                          :title="t('TAXIS.HISTORY.EDIT')"
                          @click="openEdit(row)"
                        >
                          <Icon icon="i-lucide-pencil" class="size-4" />
                        </button>
                        <button
                          type="button"
                          class="rounded-lg p-2 text-n-ruby-11 hover:bg-n-ruby-3/30"
                          :title="t('TAXIS.HISTORY.DELETE')"
                          @click="removeCourse(row)"
                        >
                          <Icon icon="i-lucide-trash-2" class="size-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="px-4 py-8 text-center text-sm text-n-slate-11">
              {{ t('TAXIS.HISTORY.EMPTY_MAIN_TABLE') }}
            </div>
          </div>
        </section>
      </div>
    </div>

    <Dialog
      ref="dialogRef"
      type="edit"
      :title="
        editing
          ? t('TAXIS.HISTORY.DIALOG_EDIT_TITLE')
          : t('TAXIS.HISTORY.DIALOG_CREATE_TITLE')
      "
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="onDialogClose"
    >
      <div class="flex flex-col gap-4">
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.REQUESTED_AT') }}
            </label>
            <input
              v-model="form.requested_at"
              type="datetime-local"
              :class="selectClass"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.STATUS') }}
            </label>
            <select v-model="form.status" :class="selectClass">
              <option
                v-for="opt in statusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <Input
            v-model.number="form.passenger_count"
            type="number"
            min="1"
            :label="t('TAXIS.HISTORY.FORM.PASSENGERS')"
          />
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.PAYMENT_STATUS') }}
            </label>
            <select v-model="form.payment_status" :class="selectClass">
              <option
                v-for="opt in paymentStatusOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </div>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.DRIVER') }}
            </label>
            <select v-model="form.taxi_driver_id" :class="selectClass">
              <option value="">
                {{ t('TAXIS.HISTORY.FORM.NONE') }}
              </option>
              <option v-for="d in driversCatalog" :key="d.id" :value="d.id">
                {{ d.name }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.CLIENT') }}
            </label>
            <select v-model="form.taxi_client_id" :class="selectClass">
              <option value="">
                {{ t('TAXIS.HISTORY.FORM.NONE') }}
              </option>
              <option v-for="c in clientsCatalog" :key="c.id" :value="c.id">
                {{ clientLabel(c) }}
              </option>
            </select>
          </div>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.PARTNER') }}
            </label>
            <select v-model="form.taxi_partner_id" :class="selectClass">
              <option value="">
                {{ t('TAXIS.HISTORY.FORM.NONE') }}
              </option>
              <option v-for="p in partnersCatalog" :key="p.id" :value="p.id">
                {{ p.name }}
              </option>
            </select>
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ t('TAXIS.HISTORY.FORM.ZONE') }}
            </label>
            <select v-model="form.taxi_zone_id" :class="selectClass">
              <option value="">
                {{ t('TAXIS.HISTORY.FORM.NONE') }}
              </option>
              <option v-for="z in zonesCatalog" :key="z.id" :value="z.id">
                {{ z.name }}
              </option>
            </select>
          </div>
        </div>
        <TextArea
          v-model="form.pickup_address"
          :label="t('TAXIS.HISTORY.FORM.PICKUP_ADDRESS')"
          :placeholder="t('TAXIS.HISTORY.FORM.ADDRESS_PLACEHOLDER')"
          min-height="4rem"
        />
        <TextArea
          v-model="form.dropoff_address"
          :label="t('TAXIS.HISTORY.FORM.DROPOFF_ADDRESS')"
          :placeholder="t('TAXIS.HISTORY.FORM.ADDRESS_PLACEHOLDER')"
          min-height="4rem"
        />
        <div class="grid gap-4 sm:grid-cols-3">
          <Input
            v-model="form.fare_amount"
            type="text"
            :label="t('TAXIS.HISTORY.FORM.FARE')"
            :placeholder="t('TAXIS.HISTORY.FORM.FARE_PLACEHOLDER')"
          />
          <Input
            v-model="form.currency"
            type="text"
            :label="t('TAXIS.HISTORY.FORM.CURRENCY')"
          />
          <Input
            v-model="form.payment_method"
            type="text"
            :label="t('TAXIS.HISTORY.FORM.PAYMENT_METHOD')"
          />
        </div>
        <TextArea
          v-model="form.notes"
          :label="t('TAXIS.HISTORY.FORM.NOTES')"
          :placeholder="t('TAXIS.HISTORY.FORM.NOTES_PLACEHOLDER')"
          :max-length="2000"
          min-height="5rem"
        />
      </div>
      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('TAXIS.HISTORY.CANCEL')"
            @click="closeDialog"
          />
          <Button
            type="button"
            :label="t('TAXIS.HISTORY.SAVE')"
            @click="saveCourse"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="assignDialogRef"
      type="edit"
      :title="t('TAXIS.HISTORY.ASSIGNMENT.DIALOG_TITLE')"
      width="md"
      :show-cancel-button="false"
      :show-confirm-button="false"
      @close="onAssignDialogClose"
    >
      <div class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{ t('TAXIS.HISTORY.ASSIGNMENT.DIALOG_HINT') }}
        </p>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TAXIS.HISTORY.FORM.DRIVER') }}
          </label>
          <select v-model="assignDriverId" :class="selectClass">
            <option value="">
              {{ t('TAXIS.HISTORY.FORM.NONE') }}
            </option>
            <option v-for="d in driversCatalog" :key="d.id" :value="d.id">
              {{ d.name }}
            </option>
          </select>
        </div>
      </div>
      <template #footer>
        <div class="flex w-full flex-wrap justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('TAXIS.HISTORY.CANCEL')"
            @click="closeAssignDialog"
          />
          <Button
            v-if="assignRow?.taxi_driver_id"
            variant="faded"
            color="ruby"
            type="button"
            :label="t('TAXIS.HISTORY.ASSIGNMENT.UNLINK')"
            @click="unlinkAssignment"
          />
          <Button
            type="button"
            :label="t('TAXIS.HISTORY.ASSIGNMENT.SAVE')"
            @click="saveAssignment"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
